import React, { useCallback } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'
import SharedOrderSummary from 'Pods/Shared/OrderSummary/OrderSummary'

import formatPrice from 'Utils/formatPrice'
import roundHumanCount from 'Utils/roundHumanCount'
import { NOOP } from 'Utils/stableDefaults'

import { getRenewalDate } from '../../Utils/dateUtils'
import PlanSelector from '../PlanSelector/PlanSelector'
import ItemContext from './Components/ItemContext/ItemContext'

const TRANSLATION_KEY_PREFIX = 'subscription_checkout.order_summary'

function OrderSummary({
  locale = 'en',
  planName,
  features,
  originalPrice = null,
  currentPrice,
  vatAmount = 0,
  onPay = NOOP,
  disabled = true,
  hidePay = false,
  selectedPlanKey = null,
  renewalIntervalMonths = 1,
  subscriptionV16Enabled = false,
  plans = null,
  onPlanChange = NOOP,
  perModelCopyEnabled = null,
  catalogValue = null,
  itemContext = null,
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const subtotal = currentPrice
  const total = currentPrice + vatAmount

  const planKeyedTranslate = (suffix, options = {}) => {
    if (!selectedPlanKey) {
      return translate(`${TRANSLATION_KEY_PREFIX}.${suffix}`, options)
    }
    return translate(`${TRANSLATION_KEY_PREFIX}.plans.${selectedPlanKey}.${suffix}`, options)
  }

  const displayedPlanName = selectedPlanKey
    ? planKeyedTranslate('name')
    : planName

  const billingPeriodLabel = planKeyedTranslate('billing_period')
  const priceUnitLabel = planKeyedTranslate('price_unit')
  const renewalText = planKeyedTranslate('renewal_note', {
    date: getRenewalDate(new Date(), locale, renewalIntervalMonths),
    price: formatPrice(currentPrice),
  })

  const featureSlots = ['first', 'second', 'third']

  const formattedCatalogValue = catalogValue ? roundHumanCount(catalogValue) : ''

  const planFeatures = selectedPlanKey
    ? featureSlots.map((slot) => (
      translate(`${TRANSLATION_KEY_PREFIX}.plans.${selectedPlanKey}.features.${slot}`, {
        modelCount: formattedCatalogValue,
      })
    ))
    : features

  const planItemCard = (
    <div className={classnames('order-summary__plan', subscriptionV16Enabled && 'order-summary__plan--outlined')}>
      <div className="order-summary__plan-content">
        <span className="cgt-body-base-semibold">{displayedPlanName}</span>
        <ul className="order-summary__features">
          {planFeatures.map((feature) => (
            <li key={feature} className="order-summary__feature">
              <span className="order-summary__feature-bullet" />
              <span className="order-summary__feature-text cgt-caption">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="order-summary__plan-pricing">
        {billingPeriodLabel && (
          <span className="order-summary__billing-period cgt-caption-semibold">
            {billingPeriodLabel}
          </span>
        )}
        <div className="order-summary__price-wrap">
          {originalPrice && (
            <span className="order-summary__original-price cgt-body-base-semibold">
              {formatPrice(originalPrice)}
            </span>
          )}
          <span className="order-summary__current-price cgt-body-lg-semibold">
            {formatPrice(currentPrice)}
          </span>
        </div>
        <span className="order-summary__price-unit cgt-caption-semibold">
          {priceUnitLabel}
        </span>
      </div>
    </div>
  )

  const legal = (
    <>
      {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_prefix`)}{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/terms-and-conditions"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_terms`)}
      </a>
      ,{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/privacy-policy"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_privacy`)}
      </a>
      {' '}{translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_connector`)}{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/subscription-fair-use-policy"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_fair_use`)}
      </a>
      .
    </>
  )

  const footer = (
    <p className="order-summary__renewal-note cgt-legacy-text-caption cgt-legacy-color-grey-40">
      {renewalText}
    </p>
  )

  return (
    <SharedOrderSummary
      title={translate(`${TRANSLATION_KEY_PREFIX}.title`)}
      subtotal={subtotal}
      subtotalLabel={translate(`${TRANSLATION_KEY_PREFIX}.subtotal`)}
      vatAmount={vatAmount}
      vatLabel={translate(`${TRANSLATION_KEY_PREFIX}.vat_included`)}
      vatTooltip={translate(`${TRANSLATION_KEY_PREFIX}.vat_tooltip`)}
      total={total}
      totalLabel={translate(`${TRANSLATION_KEY_PREFIX}.amount_due_today`)}
      payLabel={translate(`${TRANSLATION_KEY_PREFIX}.pay_button`, { amount: formatPrice(total) })}
      onPay={onPay}
      disabled={disabled}
      hidePay={hidePay}
      legal={legal}
      footer={footer}
    >
      {itemContext?.thumbnail && itemContext?.modelName && (
        <ItemContext
          thumbnail={itemContext.thumbnail}
          modelName={itemContext.modelName}
          subLabel={translate(`${TRANSLATION_KEY_PREFIX}.item_context.sub_label`)}
        />
      )}
      {subscriptionV16Enabled && plans && (
        <PlanSelector
          plans={plans}
          selectedKey={selectedPlanKey}
          onChange={onPlanChange}
          locale={locale}
          perModelCopyEnabled={perModelCopyEnabled}
        />
      )}
      {planItemCard}
    </SharedOrderSummary>
  )
}

OrderSummary.propTypes = {
  locale: string,
  planName: string.isRequired,
  features: arrayOf(string).isRequired,
  originalPrice: number,
  currentPrice: number.isRequired,
  vatAmount: number,
  onPay: func,
  disabled: bool,
  hidePay: bool,
  selectedPlanKey: string,
  renewalIntervalMonths: number,
  subscriptionV16Enabled: bool,
  plans: arrayOf(shape({ key: string, price: number })),
  onPlanChange: func,
  perModelCopyEnabled: bool,
  catalogValue: string,
  itemContext: shape({ thumbnail: string, modelName: string }),
}

export default OrderSummary
